import { Form, Layout, Spin } from 'antd';
import { Content } from 'antd/es/layout/layout';
import { useCallback, useContext, useEffect, useReducer } from 'react';

import { SCard, SDetailLayout } from 'app/components/StaycationUI';
import { CONTACTS_SECTIONS } from 'app/components/commons/Hotel/Contacts/ContactSection';
import { Mode } from 'app/components/commons/Hotel/Types';
import { HotelContext } from 'app/context/HotelContext/HotelContext';
import { TaskCenterContext } from 'app/context/TaskCenterContext/TaskCenterContext';
import {
  ViewEditModeContext,
  ViewEditModeContextProvider,
} from 'app/context/ViewEditModeContext/ViewEditModeContext';
import { useHotelContacts } from 'app/hooks/data/core/extranet/hotel/useHotelContacts';
import { useUpdateHotelContacts } from 'app/hooks/data/core/extranet/hotel/useUpdateHotelContacts';
import type { ExtranetHotelContacts } from 'app/typings/core/extranet/hotel/contacts';
import { SCROLL_TO_FIRST_ERROR } from 'app/utils/scroll';

import { ExtranetHeader } from '../../commons/ExtranetHeader/ExtranetHeader';

import styles from './ExtranetContact.module.scss';

const PAGE = 'Contact';

const ExtranetContactComponents = () => {
  const { setPage } = useContext(TaskCenterContext);
  const { hotelId } = useContext(HotelContext);
  const { mode, isViewMode, enterViewMode, enterEditMode } =
    useContext(ViewEditModeContext);

  const [form] = Form.useForm<ExtranetHotelContacts>();

  const [, forceUpdate] = useReducer((count: number) => count + 1, 0);

  const { data: hotelContacts, isLoading: isHotelContactsLoading } =
    useHotelContacts(hotelId);
  const { mutateAsync: updatedHotelContacts } = useUpdateHotelContacts(hotelId);

  useEffect(() => {
    setPage(PAGE);
  }, [setPage]);

  const onFinish = useCallback(
    (values: ExtranetHotelContacts) => {
      updatedHotelContacts(values);
      enterViewMode();
    },
    [enterViewMode, updatedHotelContacts]
  );

  const onSave = useCallback(() => {
    form.submit();
  }, [form]);

  const onCancel = useCallback(() => {
    enterViewMode();
    form.resetFields();

    if (hotelContacts) {
      form.setFieldsValue(hotelContacts);
    }
  }, [enterViewMode, form, hotelContacts]);

  useEffect(() => {
    if (hotelContacts) {
      form.setFieldsValue(hotelContacts);
    }
  }, [hotelContacts, form]);

  return (
    <Layout className={styles.extranetContact}>
      <ExtranetHeader page={PAGE} />

      <Form
        className={styles.form}
        form={form}
        onFinish={onFinish}
        onFieldsChange={forceUpdate}
        disabled={mode === Mode.View}
        scrollToFirstError={SCROLL_TO_FIRST_ERROR}
      >
        <SDetailLayout
          isDirty={form.isFieldsTouched()}
          className={styles.extranetContactLayout}
          mode={mode}
          onEdit={enterEditMode}
          onSave={onSave}
          onCancel={onCancel}
          saveDisabled={form
            .getFieldsError()
            .some((item) => item.errors.length > 0)}
        >
          <Spin
            spinning={isHotelContactsLoading}
            wrapperClassName={styles.mainContent}
          >
            <Content className={styles.content}>
              {CONTACTS_SECTIONS.map((section) => {
                const SectionComponent = section.component;

                return (
                  <SCard
                    key={section.key}
                    title={section.title}
                    subtitle={section.subtitle}
                    className={styles.contactSection}
                  >
                    <SectionComponent
                      isEditMode={!isViewMode}
                      hotelId={hotelId}
                      form={form}
                      hideComment
                    />
                  </SCard>
                );
              })}
              <div className={styles.bottomSpacer} />
            </Content>
          </Spin>
        </SDetailLayout>
      </Form>
    </Layout>
  );
};

export const ExtranetContact = () => {
  return (
    <ViewEditModeContextProvider>
      <ExtranetContactComponents />
    </ViewEditModeContextProvider>
  );
};
